import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, ShieldQuestion, X } from 'lucide-react';
import { t } from '../i18n';
import { lockBodyScroll } from '../utils/bodyScrollLock';
import './ConfirmDialog.css';

function ConfirmDialog({
  isOpen,
  title = t('common.confirmTitle'),
  message = '',
  confirmLabel = t('common.ok'),
  cancelLabel = t('common.cancel'),
  tone = 'warning',
  mode = 'confirm',
  onConfirm,
  onCancel,
  keyboardConfirmOnAnyKey = false,
  children,
}) {
  const dialogRef = useRef(null);
  const titleId = useId();
  const callbacks = useRef({ onConfirm, onCancel });
  callbacks.current = { onConfirm, onCancel };
  useEffect(() => {
    if (!isOpen) return undefined;
    const previousFocus = document.activeElement;
    const releaseScrollLock = lockBodyScroll();
    const initialFocus = dialogRef.current?.querySelector('.confirm-dialog-cancel, .confirm-dialog-confirm');
    (initialFocus || dialogRef.current)?.focus();
    const handleKeyDown = (event) => {
      if (event.repeat) return;
      if (event.key === 'Tab') {
        const controls = Array.from(dialogRef.current?.querySelectorAll('button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), a[href]') || []);
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (!first) { event.preventDefault(); return; }
        if (event.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) {
          event.preventDefault(); last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault(); first.focus();
        }
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (mode === 'alert') callbacks.current.onConfirm?.();
        else callbacks.current.onCancel?.();
        return;
      }
      if (mode !== 'alert' && keyboardConfirmOnAnyKey) {
        event.preventDefault();
        event.stopImmediatePropagation();
        callbacks.current.onConfirm?.();
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      releaseScrollLock();
      if (previousFocus?.isConnected) previousFocus.focus?.();
    };
  }, [isOpen, keyboardConfirmOnAnyKey, mode]);

  if (!isOpen) return null;

  const isNotice = mode === 'alert';
  const Icon = isNotice ? CheckCircle2 : tone === 'danger' ? AlertTriangle : ShieldQuestion;

  return (
    <div className="confirm-dialog-overlay" onClick={isNotice ? onConfirm : onCancel}>
      <div
        ref={dialogRef}
        tabIndex={-1}
        className={`confirm-dialog confirm-dialog-${tone}`.trim()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="confirm-dialog-close"
          onClick={isNotice ? onConfirm : onCancel}
          aria-label={t('common.close')}
        >
          <X size={20} strokeWidth={2.4} aria-hidden="true" />
        </button>
        <div className="confirm-dialog-icon">
          <Icon size={34} strokeWidth={2.2} aria-hidden="true" />
        </div>
        <div className="confirm-dialog-copy">
          <h2 id={titleId}>{title}</h2>
          {message && <p>{message}</p>}
          {children}
        </div>
        <div className="confirm-dialog-actions">
          {!isNotice && (
            <button type="button" className="confirm-dialog-button confirm-dialog-cancel" onClick={onCancel}>
              {cancelLabel}
            </button>
          )}
          <button type="button" className="confirm-dialog-button confirm-dialog-confirm" onClick={onConfirm}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export function useConfirmDialog() {
  const [dialog, setDialog] = useState(null);

  const openDialog = useCallback((options = {}, mode = 'confirm') => new Promise((resolve) => {
    setDialog({
      ...options,
      mode,
      resolve,
    });
  }), []);

  const closeDialog = useCallback((result) => {
    setDialog((current) => {
      if (current?.resolve) {
        current.resolve(result);
      }
      return null;
    });
  }, []);

  const confirm = useCallback((options) => openDialog(options, 'confirm'), [openDialog]);
  const alert = useCallback((options) => openDialog(options, 'alert'), [openDialog]);

  const confirmDialog = (
    <ConfirmDialog
      isOpen={Boolean(dialog)}
      title={dialog?.title}
      message={dialog?.message}
      confirmLabel={dialog?.confirmLabel}
      cancelLabel={dialog?.cancelLabel}
      tone={dialog?.tone}
      mode={dialog?.mode}
      keyboardConfirmOnAnyKey={dialog?.keyboardConfirmOnAnyKey}
      onConfirm={() => closeDialog(true)}
      onCancel={() => closeDialog(false)}
    />
  );

  return { confirm, alert, confirmDialog, isOpen: Boolean(dialog) };
}

export default ConfirmDialog;
